/**
 * Best-effort human-readable brand name from a domain. Drops the TLD,
 * picks the registrable root, and capitalizes word tokens.
 *
 * Examples:
 *   stripe.com         -> Stripe
 *   mail.intercom.com  -> Intercom
 *   notion.so          -> Notion
 *   my-store.com       -> My Store
 *   bbc.co.uk          -> Bbc
 *   example.com.au     -> Example
 *
 * Falls back to the original input if parsing yields nothing usable.
 * Pure and relative-imported so Convex reads can use it too.
 */
import { capitalize } from '../shared/text/capitalize'

// Known compound (second-level) public suffixes per ccTLD. When the last two
// labels match one of these pairs, the registrable root is the third-to-last
// label rather than the second-to-last. Sourced from the most common entries
// in the IANA / Public Suffix List that show up in real customer domains.
// Keep this list pragmatic, not exhaustive — over-matching would corrupt
// names for plain `.com` brands like `go.com`.
const COMPOUND_PUBLIC_SUFFIXES: Record<string, ReadonlySet<string>> = {
  uk: new Set(['co', 'org', 'ac', 'gov', 'me', 'net', 'plc', 'ltd', 'sch']),
  au: new Set(['com', 'net', 'org', 'edu', 'gov', 'asn', 'id']),
  nz: new Set(['co', 'net', 'org', 'ac', 'govt', 'school']),
  jp: new Set(['co', 'ne', 'ac', 'or', 'go', 'ad', 'ed', 'gr', 'lg']),
  kr: new Set(['co', 'ne', 'or', 're', 'pe', 'go', 'ac']),
  in: new Set(['co', 'org', 'net', 'ac', 'gov', 'edu', 'res']),
  br: new Set(['com', 'net', 'org', 'gov', 'edu']),
  mx: new Set(['com', 'net', 'org', 'edu', 'gob']),
  za: new Set(['co', 'org', 'net', 'ac', 'gov', 'web']),
  sg: new Set(['com', 'net', 'org', 'gov', 'edu']),
  hk: new Set(['com', 'net', 'org', 'gov', 'edu']),
  tw: new Set(['com', 'net', 'org', 'gov', 'edu']),
  tr: new Set(['com', 'net', 'org', 'gov', 'edu']),
  il: new Set(['co', 'net', 'org', 'gov', 'ac', 'k12', 'muni']),
  cn: new Set(['com', 'net', 'org', 'gov', 'edu', 'ac']),
  ar: new Set(['com', 'net', 'org', 'gov', 'edu']),
  ph: new Set(['com', 'net', 'org', 'gov', 'edu']),
  vn: new Set(['com', 'net', 'org', 'gov', 'edu']),
  my: new Set(['com', 'net', 'org', 'gov', 'edu']),
  th: new Set(['co', 'in', 'ac', 'go', 'mi', 'or', 'net']),
  id: new Set(['co', 'net', 'or', 'ac', 'go', 'web', 'sch']),
  ve: new Set(['com', 'net', 'org', 'gob', 'edu']),
  pe: new Set(['com', 'net', 'org', 'gob', 'edu']),
}

function isCompoundPublicSuffix(tld: string, sld: string): boolean {
  return COMPOUND_PUBLIC_SUFFIXES[tld]?.has(sld) ?? false
}

/**
 * The registrable root label of a domain, lowercased: `mail.intercom.com` ->
 * `intercom`, `bbc.co.uk` -> `bbc`. `null` when nothing usable is left.
 */
export function registrableDomainLabel(domain: string): string | null {
  const parts = domain.trim().toLowerCase().split('.').filter(Boolean)
  if (parts.length === 0) {
    return null
  }
  let rootIndex = 0
  if (parts.length >= 3) {
    const tld = parts[parts.length - 1]!
    const sld = parts[parts.length - 2]!
    rootIndex = isCompoundPublicSuffix(tld, sld)
      ? parts.length - 3
      : parts.length - 2
  }
  return parts[rootIndex] || null
}

export function formatBrandNameFromDomain(domain: string): string {
  const root = registrableDomainLabel(domain)
  if (!root) {
    return domain
  }

  const formatted = root.split(/[-_]/).filter(Boolean).map(capitalize).join(' ')

  return formatted || domain
}

/**
 * Whether a stored brand name is really just the domain: the domain itself,
 * any dotted host, or the domain's bare registrable label in all lowercase
 * ("retool" for retool.com). A cased label ("GitHub", "Crocs") is a name
 * someone chose and is never treated as a domain.
 */
export function isDomainLikeBrandName(name: string, domain: string): boolean {
  const trimmed = name.trim()
  if (!trimmed) {
    return true
  }
  const lower = trimmed.toLowerCase()
  const canonicalDomain = domain.trim().toLowerCase()
  if (lower === canonicalDomain) {
    return true
  }
  if (/^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/.test(lower)) {
    return true
  }
  const root = canonicalDomain.split('.')[0] ?? ''
  return root.length > 0 && trimmed === root
}

/**
 * The name a gallery surface shows for a brand: the curated `templateBrands`
 * name, else the source's display name, either one read through
 * `formatBrandNameFromDomain` when it is only the domain — so a card says
 * "Ramp", never "ramp.com", while a chosen name is kept exactly.
 */
export function resolveBrandDisplayName(input: {
  storedName: string
  brandDomain: string
  curatedName?: string | null
}): string {
  const domain = input.brandDomain.trim()
  for (const candidate of [input.curatedName ?? '', input.storedName]) {
    const trimmed = candidate.trim()
    if (trimmed && !isDomainLikeBrandName(trimmed, domain)) {
      return trimmed
    }
  }
  const formatted = formatBrandNameFromDomain(domain || input.storedName)
  return formatted || input.storedName.trim() || domain
}
